<script>
import { useCatalogStore } from "@js/store/CatalogStore.js";
import VueSlider from "vue-3-slider-component";
import FormattedNumberInput from "@js/components/Input/FormattedNumberInput.vue";
import CatalogFilterBlockLoading from "@js/components/Sections/Catalog/Components/CatalogFilterBlockLoading.vue";
import CatalogFilterBlock from "@js/components/Sections/Catalog/Components/CatalogFilterBlock.vue";
import CatalogFilterLoading from "@js/components/Sections/Catalog/Components/CatalogFilterLoading.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import CustomCheckbox from "@js/components/Input/CustomCheckbox.vue";
import { isMobile } from "@js/helpers.ts";
import IconFilterClear from "@js/components/Icons/IconFilterClear.vue";

export default {
    name: "CatalogFilter",
    components: {
        IconFilterClear,
        CustomCheckbox,
        NumberFormatComponent,
        CatalogFilterLoading,
        CatalogFilterBlock,
        CatalogFilterBlockLoading,
        FormattedNumberInput,
        VueSlider
    },

    data() {
        return {
            reset_hovered: false,
        };
    },

    setup() {
        const catalogStore = useCatalogStore();
        return { catalogStore };
    },

    computed: {
        // устройство
        isMobileDevice() {
            return typeof isMobile === "function" ? isMobile() : !!isMobile;
        },

        // лимиты цены от бэка
        priceMin() {
            const l = this.catalogStore.filter_data?.limits?.price;
            return Number(Array.isArray(l) ? l[0] : (this.catalogStore.filter_data?.limits?.min_price ?? 0));
        },
        priceMax() {
            const l = this.catalogStore.filter_data?.limits?.price;
            return Number(Array.isArray(l) ? l[1] : (this.catalogStore.filter_data?.limits?.max_price ?? 0));
        },

        // алиасы фасетов
        categories() {
            return this.catalogStore.filter_data?.categories || [];
        },
        subCategories() {
            return this.catalogStore.filter_data?.sub_categories || [];
        },
        characteristics() {
            return this.catalogStore.filter_data?.characteristics || [];
        },
    },

    watch: {
        // подкатегории — сразу применяем фильтр
        'catalogStore.selected_sub_categories': {
            deep: true,
            handler() {
                this.catalogStore.pagination.current_page = 1;
                this.catalogStore.applyFiltersDebounced();
            }
        },
        // цена — при изменении ползунка/инпутов
        'catalogStore.filter.price': {
            deep: true,
            handler() {
                if (!this.catalogStore.is_filter_watcher_on) {
                    return;
                }
                this.catalogStore.pagination.current_page = 1;
                this.catalogStore.applyFiltersDebounced();
            }
        },
    },

    mounted() {
        // гарантируем корректный диапазон
        if (!Array.isArray(this.catalogStore.filter.price) || this.catalogStore.filter.price.length !== 2) {
            this.catalogStore.filter.price = [this.priceMin, this.priceMax];
        } else {
            this.clampPrice();
        }
    },

    methods: {
        // поиск по массиву опций
        filterOptionsByQuery(options, query) {
            const list = options || [];
            if (!query || list.length <= 7) return list;
            const q = query.toLowerCase();
            return list.filter(o => (o.title || o.id || "").toLowerCase().includes(q));
        },

        // поиск по значениям характеристик
        filterCharValues(values, query) {
            const arr = values || [];
            const q = (query || "").trim().toLowerCase();
            if (!q) return arr;
            return arr.filter(v => (v.value || "").toLowerCase().includes(q));
        },

        // применяем массив значений характеристики
        updateCharacteristic(typeId, newArr) {
            this.catalogStore.selected_characteristics = {
                ...this.catalogStore.selected_characteristics,
                [typeId]: Array.isArray(newArr) ? newArr : [],
            };
            this.catalogStore.pagination.current_page = 1;
            this.catalogStore.applyFiltersDebounced();
        },

        clampPrice() {
            let [from, to] = this.catalogStore.filter.price || [this.priceMin, this.priceMax];
            from = Math.max(this.priceMin, Math.min(from ?? this.priceMin, this.priceMax));
            to   = Math.max(this.priceMin, Math.min(to   ?? this.priceMax,   this.priceMax));
            if (from > to) [from, to] = [to, from];
            this.catalogStore.filter.price = [from, to];
        },

        resetAll() {
            this.catalogStore.resetFilter();
        },
    }
};
</script>

<template>
    <div class="w-full" v-if="catalogStore.loadings.filter_first_load">
        <!-- Верхняя плашка -->
        <div class="relative flex items-center justify-between yellow-border pb-2 border-b border-gray-200"
             :class="{'yellow-border-rtl': reset_hovered}">
            <div class="flex items-center gap-1 font-medium" v-if="catalogStore.loadings.catalog_loaded">
                Найдено:
                <NumberFormatComponent :number="catalogStore.total_result ?? 0"/>
            </div>
            <div v-else>
                <div role="status" class="max-w-sm animate-pulse">
                    <div class="h-6 bg-gray-300 rounded w-24 animate-pulse"></div>
                </div>
            </div>

            <div @click.prevent="resetAll"
                 @mouseenter="reset_hovered = true"
                 @mouseleave="reset_hovered = false"
                 class="font-sm font-semibold flex items-center fill-black cursor-pointer transition">
                <IconFilterClear class="w-5 h-5 me-1"/>
                Сбросить
            </div>
        </div>

        <!-- Цена -->
        <CatalogFilterBlock title="Цена">
            <div v-if="catalogStore.loadings.filter_limits_loaded" class="px-2">
                <VueSlider v-model="catalogStore.filter.price"
                           :min="catalogStore.filter_data.limits.price[0]"
                           :max="catalogStore.filter_data.limits.price[1]"
                           tooltip="none"/>
                <div class="grid grid-cols-2 gap-5 mt-4">
                    <div class="relative z-0 w-full group">
                        <FormattedNumberInput placeholder=" " name="cf_price_from" id="cf_price_from"
                                              :min="catalogStore.filter_data.limits.price[0]"
                                              :max="catalogStore.filter_data.limits.price[1]"
                                              v-model="catalogStore.filter.price[0]"
                                              class="block pt-2 pb-1 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b border-gray-500 appearance-none  focus:outline-none focus:ring-0 focus:border-indigo-600 peer"/>
                        <label for="cf_price_from"
                               class="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0  peer-focus:text-indigo-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 left-0">
                            Цена от
                        </label>
                    </div>
                    <div class="relative z-0 w-full group">
                        <FormattedNumberInput name="cf_price_to" id="cf_price_to" placeholder=" "
                                              :min="catalogStore.filter_data.limits.price[0]"
                                              :max="catalogStore.filter_data.limits.price[1]"
                                              v-model="catalogStore.filter.price[1]"
                                              class="block pt-2 pb-1 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b border-gray-500 appearance-none  focus:outline-none focus:ring-0 focus:border-indigo-600 peer"
                        />
                        <label for="cf_price_to"
                               class="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0  peer-focus:text-indigo-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 left-0">
                            Цена до
                        </label>
                    </div>
                </div>
            </div>
            <CatalogFilterBlockLoading v-else/>
        </CatalogFilterBlock>

        <!-- Подкатегории -->
        <CatalogFilterBlock
            title="Подкатегории"
            :enableSearch="subCategories.length > 7"
            searchPlaceholder="Поиск по подкатегории"
            v-slot="{ searchQuery }"
            :default_open="!isMobileDevice"
            v-if="subCategories.length"
        >
            <div
                v-for="(item, key) in filterOptionsByQuery(subCategories, searchQuery)"
                :key="'subcat-'+key"
                class="py-2"
            >
                <div class="flex items-center">
                    <CustomCheckbox
                        :id="'filter-subcats-' + key"
                        :value="item.id"
                        v-model="catalogStore.selected_sub_categories"
                    />
                    <label :for="'filter-subcats-' + key"
                           class="flex items-center justify-between w-full ms-2 text-sm font-medium text-gray-900 cursor-pointer">
                        <span>{{ item.title }}</span>
                        <span class="text-sm font-medium text-gray-900">({{ item.count ?? 0 }})</span>
                    </label>
                </div>
            </div>
            <p v-show="!filterOptionsByQuery(subCategories, searchQuery).length"
               class="text-sm text-gray-500 pt-2">Ничего не найдено</p>
        </CatalogFilterBlock>

        <!-- Динамические характеристики -->
        <template v-for="type in characteristics" :key="'type-'+type.type_id">
            <CatalogFilterBlock
                :title="type.type_name || ('Характеристика #' + type.type_id)"
                :enableSearch="(type.values?.length || 0) > 7"
                searchPlaceholder="Поиск по значению"
                v-slot="{ searchQuery }"
                :default_open="false"
            >
                <div
                    v-for="(val, idx) in filterCharValues(type.values || [], searchQuery)"
                    :key="'val-'+type.type_id+'-'+idx"
                    class="py-2"
                >
                    <div class="flex items-center">
                        <CustomCheckbox
                            :id="'char-' + type.type_id + '-' + idx"
                            :value="val.value"
                            :modelValue="catalogStore.selected_characteristics[type.type_id] || []"
                            @update:modelValue="arr => updateCharacteristic(type.type_id, arr)"
                        />
                        <label :for="'char-' + type.type_id + '-' + idx"
                               class="flex items-center justify-between w-full ms-2 text-sm font-medium text-gray-900 cursor-pointer">
                            <span class="flex items-center gap-2">{{ val.value }}</span>
                            <span class="text-sm font-medium text-gray-900">({{ val.count ?? 0 }})</span>
                        </label>
                    </div>
                </div>
                <p v-show="!filterCharValues(type.values || [], searchQuery).length"
                   class="text-sm text-gray-500 pt-2">Ничего не найдено</p>
            </CatalogFilterBlock>
        </template>
    </div>

    <CatalogFilterLoading v-else/>
</template>

<style scoped lang="scss">
.yellow-border:before {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100px;
    height: 2px;
    background: var(--main-color);
    transition: left .3s;
}
.yellow-border-rtl:before {
    left: calc(100% - 100px);
}
</style>
